import React, { useState } from 'react';
import { Copy, Check, Code2, Globe } from 'lucide-react';

interface EmbedCodeGeneratorProps {
  chatbotId: string;
  campaignSlug?: string;
  baseUrl?: string;
}

export function EmbedCodeGenerator({
  chatbotId,
  campaignSlug,
  baseUrl = 'https://intentdrive.ai',
}: EmbedCodeGeneratorProps) {
  // If we have a campaign slug, use the standalone campaign URL which renders ScoringWidget.
  // Otherwise fall back to /c/{chatbotId} pattern.
  const embedPath = campaignSlug ? `/c/${campaignSlug}` : `/c/${chatbotId}`;
  const embedUrl = `${baseUrl}${embedPath}`;

  const iframeCode = `<iframe
  src="${embedUrl}"
  width="100%"
  height="700"
  frameborder="0"
  style="border: none; border-radius: 12px; max-width: 640px;"
  allow="clipboard-write"
></iframe>`;

  const scriptCode = `<div id="intentdrive-widget"></div>
<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${embedUrl}';
  iframe.style.cssText = 'border:none;width:100%;max-width:640px;height:700px;border-radius:12px;';
  iframe.allow = 'clipboard-write';
  document.getElementById('intentdrive-widget').appendChild(iframe);
})();
</script>`;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <div className="space-y-6 text-slate-200">
      {/* Iframe embed */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-white">Iframe embed (recommended)</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Paste this iframe into your page where you want the scoring widget to appear.
        </p>
        <div className="relative rounded-lg border border-slate-800 bg-slate-950">
          <pre className="p-4 pr-24 text-xs text-emerald-200 overflow-x-auto whitespace-pre-wrap break-all">
            {iframeCode}
          </pre>
          <button
            type="button"
            onClick={() => copy('iframe', iframeCode)}
            className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition-colors"
          >
            {copiedKey === 'iframe' ? (
              <>
                <Check className="w-3.5 h-3.5" /> Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" /> Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Script tag */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Code2 className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-white">Script embed</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Paste this snippet where you want the widget to render. It creates an iframe automatically.
        </p>
        <div className="relative rounded-lg border border-slate-800 bg-slate-950">
          <pre className="p-4 pr-24 text-xs text-emerald-200 overflow-x-auto whitespace-pre-wrap break-all">
            {scriptCode}
          </pre>
          <button
            type="button"
            onClick={() => copy('script', scriptCode)}
            className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition-colors"
          >
            {copiedKey === 'script' ? (
              <>
                <Check className="w-3.5 h-3.5" /> Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" /> Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview instructions */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
        <h4 className="text-sm font-semibold text-emerald-300 mb-2">Preview instructions</h4>
        <ol className="list-decimal list-inside space-y-1 text-xs text-slate-300">
          <li>Copy the snippet you want to use.</li>
          <li>Paste it into your website's HTML where you want the widget.</li>
          <li>Reload your page to see the scoring widget appear.</li>
          <li>
            To test without deploying, open{' '}
            <code className="text-emerald-400">{embedUrl}</code> directly in your browser.
          </li>
        </ol>
      </div>
    </div>
  );
}

export default EmbedCodeGenerator;
